import { RefObject, useEffect } from 'react';

const transitionTimePerPixel = 0.02;

export const useScrollText = (
  textBlockRef: RefObject<HTMLElement>,
  triggerBlockRef?: RefObject<HTMLElement>,
  enabled = true,
) => {
  useEffect(() => {
    if (!enabled) return;

    const textBlock = textBlockRef.current;
    const triggerBlock = triggerBlockRef ? triggerBlockRef.current : textBlockRef.current;
    if (!textBlock || !triggerBlock) return;

    // <input>/<textarea> can't be scrolled by translating a child (they have
    // none) — they scroll their own text via `scrollLeft`. Animate that with
    // rAF at the same linear speed the transform path uses.
    const isFormField =
      textBlock instanceof HTMLInputElement || textBlock instanceof HTMLTextAreaElement;

    let rafId = 0;

    const animateScrollLeft = (el: HTMLElement, target: number) => {
      cancelAnimationFrame(rafId);
      const from = el.scrollLeft;
      const distance = target - from;
      if (!distance) return;
      const duration = Math.abs(distance) * transitionTimePerPixel * 1000;
      let startTs = 0;
      const step = (ts: number) => {
        if (!startTs) startTs = ts;
        const t = duration <= 0 ? 1 : Math.min((ts - startTs) / duration, 1);
        el.scrollLeft = from + distance * t;
        if (t < 1) rafId = requestAnimationFrame(step);
      };
      rafId = requestAnimationFrame(step);
    };

    const handleMouseEnter = () => {
      if (isFormField) {
        // Read the real max scroll offset without trusting scrollWidth (some
        // engines report it equal to clientWidth for inputs): overshoot, let
        // the browser clamp, capture, then restore in the same frame.
        const prev = textBlock.scrollLeft;
        textBlock.scrollLeft = 1e6;
        const max = textBlock.scrollLeft;
        textBlock.scrollLeft = prev;
        if (max > 0) animateScrollLeft(textBlock, max);
        return;
      }

      if (textBlock.lastChild instanceof HTMLElement) {
        const textWidth = textBlock.lastChild.clientWidth;
        const boxWidth = parseFloat(getComputedStyle(textBlock).width);
        const translateVal = Math.min(boxWidth - textWidth, 0);
        const translateTime = -transitionTimePerPixel * translateVal + 's';
        textBlock.lastChild.style.transitionDuration = translateTime;

        textBlock.lastChild.style.transitionProperty = `transform`;
        textBlock.lastChild.style.transform = `translateX(${translateVal}px)`;
        textBlock.lastChild.style.transitionTimingFunction = `linear`;
        textBlock.lastChild.style.whiteSpace = `nowrap`;
      }
    };

    const handleMouseLeave = () => {
      if (isFormField) {
        // Snap back instantly, mirroring the transform path's `0s` reset.
        cancelAnimationFrame(rafId);
        textBlock.scrollLeft = 0;
        return;
      }

      if (textBlock.lastChild instanceof HTMLElement) {
        (textBlock.lastChild as HTMLElement).style.transform = 'translateX(0)';
        textBlock.lastChild.style.transitionDuration = '0s';
      }
    };

    triggerBlock.addEventListener('mouseenter', handleMouseEnter);
    triggerBlock.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      cancelAnimationFrame(rafId);
      triggerBlock.removeEventListener('mouseenter', handleMouseEnter);
      triggerBlock.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [enabled]);
};
